import React, { useState, useCallback } from 'react';
import { BoardPost } from '../types/board';
import { memoNamed } from '../utils/react/memo-named';
import {
  Post,
  PostName,
  PostTextarea,
  PostTime,
  PostTitle,
  PostUpdating,
} from './common/styledBoard';
import ReactMarkdown from 'react-markdown';
import { TimestampToString } from './common/firebase';
import firebase from 'firebase';
import 'firebase/firestore';
import 'firebase/auth';

interface Props {
  isAdmin: boolean;
  postInfo: { post: BoardPost; docID: string };
  setPostInfoList: (
    fn: (
      postInfoList: { post: BoardPost; docID: string }[]
    ) => { post: BoardPost; docID: string }[]
  ) => void;
}

export const BoardViewInfo = memoNamed<Props>('BoardViewInfo', (props) => {
  const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
  const [editable, setEditable] = useState<boolean>(false);
  const [edited, setEdited] = useState<boolean>(false);
  const [text, setText] = useState<string>(props.postInfo.post.text);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const toggleConfirm = useCallback(() => {
    setDeleteConfirm((deleteConfirm) => !deleteConfirm);
  }, []);

  const deletePost = useCallback(() => {
    const db = firebase.firestore();
    setIsUpdating(true);
    db.collection('Board')
      .doc(props.postInfo.docID)
      .update({ isPublic: false })
      .then(() => {
        toggleConfirm();
        setIsUpdating(false);
        props.setPostInfoList((postInfoList) =>
          postInfoList.filter((p) => p !== props.postInfo)
        );
      })
      .catch((error) => {
        console.error(error);
        setIsUpdating(false);
      });
  }, [props, toggleConfirm]);

  const updateText = useCallback(() => {
    const db = firebase.firestore();
    setEditable(false);
    setEdited(false);
    setIsUpdating(true);
    db.collection('Board')
      .doc(props.postInfo.docID)
      .update({ text: text })
      .then(() => {
        setIsUpdating(false);
        setEdited(true);
      })
      .catch((error: any) => {
        console.error(error);
        setIsUpdating(false);
      });
  }, [props, text]);

  const startEdit = useCallback(() => {
    setEditable(true);
  }, []);

  const onChangeText = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setEdited(false);
      setText(event.target.value);
    },
    []
  );

  const postStyle = edited ? { borderColor: 'red', borderWidth: '3px' } : {};

  return (
    <Post style={postStyle}>
      <PostTitle>{props.postInfo.post.title}</PostTitle>
      <PostName>by {props.postInfo.post.name}</PostName>
      <PostUpdating>
        {edited ? '【保存済】' : isUpdating ? '通信中...' : undefined}
      </PostUpdating>
      <PostTime>{TimestampToString(props.postInfo.post.postTime)}</PostTime>
      <br />
      {editable ? (
        <PostTextarea
          onBlur={updateText}
          onChange={onChangeText}
          value={text}
          rows={10}
        />
      ) : (
        <ReactMarkdown source={text} />
      )}
      {(props.isAdmin ||
        props.postInfo.post.userID === firebase.auth().currentUser?.uid) ??
      '' ? (
        <div>
          {deleteConfirm ? (
            <div>
              本当に削除しますか？<button onClick={deletePost}>削除する</button>
              <button onClick={toggleConfirm}>いいえ</button>
            </div>
          ) : (
            <button onClick={toggleConfirm}>削除</button>
          )}
          {!editable ? <button onClick={startEdit}>編集</button> : undefined}
        </div>
      ) : undefined}
    </Post>
  );
});
